import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = [
  { value: "candidato", label: "Candidato(a)" },
  { value: "coordenador", label: "Coordenador / general do QG" },
  { value: "partido", label: "Dirigente partidário / diretório" },
  { value: "marqueteiro", label: "Marqueteiro / consultor" },
  { value: "agencia", label: "Agência" },
  { value: "outro", label: "Outro" },
];

const SCOPES = [
  { value: "majoritario", label: "Majoritário" },
  { value: "proporcional", label: "Proporcional" },
  { value: "partido", label: "Partido / diretório" },
  { value: "outro", label: "Outro" },
];

const INITIAL = {
  name: "",
  role: "",
  organization: "",
  candidate_name: "",
  email: "",
  phone: "",
  state: "",
  scope: "",
  message: "",
};

export default function LeadForm() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!form.name || !form.role || !form.email || !form.phone) {
      toast.error("Preencha nome, perfil, e-mail e telefone.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        organization: form.organization || null,
        candidate_name: form.candidate_name || null,
        state: form.state || null,
        scope: form.scope || null,
        message: form.message || null,
      });
      setDone(true);
      toast.success("Recebemos seu contato. Em breve, retornaremos.");
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Não foi possível enviar agora. Tente novamente em instantes.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contato"
      data-testid="lead-form-section"
      className="relative py-24 sm:py-32 bg-[#1B1D1D] text-[#F5FAFB] overflow-hidden"
    >
      <div
        className="absolute -top-32 -left-20 h-[460px] w-[460px] rounded-full blur-3xl opacity-25"
        style={{
          background: "radial-gradient(closest-side, #208BFF, transparent 70%)",
        }}
      />
      <div
        className="absolute -bottom-32 -right-20 h-[400px] w-[400px] rounded-full blur-3xl opacity-20"
        style={{
          background: "radial-gradient(closest-side, #7E20FF, transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#208BFF]">
            Vamos conversar
          </span>
          <h2
            data-testid="lead-form-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[0.96] text-[#F5FAFB]"
          >
            Minha base na RSM
          </h2>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#F5FAFB]/70 max-w-md">
            Conte um pouco sobre a sua operação. Em até 48 horas úteis, alguém
            da RSM Politics retorna com uma proposta de diagnóstico.
          </p>

          <div className="mt-10 space-y-4 text-sm text-[#F5FAFB]/75 max-w-sm">
            {[
              "Você fala diretamente com o time de Brasília.",
              "Atendemos campanhas de alto impacto — governos, majoritários e proporcionais relevantes.",
              "Operamos com sigilo absoluto sobre dados e estratégia.",
            ].map((t, i) => (
              <div key={t} className="flex items-start gap-3">
                <CheckCircle2
                  size={18}
                  className="mt-0.5 text-[#3ED383] flex-shrink-0"
                />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div
              data-testid="lead-form-success"
              className="rounded-2xl border border-[#3ED383]/30 bg-white/5 backdrop-blur p-10 text-center"
            >
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#3ED383]/20 text-[#3ED383]">
                <CheckCircle2 size={28} />
              </div>
              <h3 className="mt-6 font-display text-2xl font-semibold text-[#F5FAFB]">
                Recebemos seu contato.
              </h3>
              <p className="mt-3 text-[#F5FAFB]/70 max-w-md mx-auto">
                Em até 48 horas úteis, alguém da RSM Politics retorna com uma
                proposta de diagnóstico personalizada para a sua operação.
              </p>
              <button
                data-testid="lead-form-reset"
                onClick={() => setDone(false)}
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-[#F5FAFB]/85 hover:border-[#208BFF] hover:text-[#208BFF] transition-colors"
              >
                Enviar outro contato
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="lead-form"
              className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-6 sm:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Seu nome *">
                  <input
                    data-testid="lead-input-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Nome completo"
                    className={inputCls}
                  />
                </Field>
                <Field label="Perfil *">
                  <select
                    data-testid="lead-input-role"
                    required
                    value={form.role}
                    onChange={set("role")}
                    className={inputCls}
                  >
                    <option value="" disabled>
                      Selecione…
                    </option>
                    {ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="E-mail *">
                  <input
                    data-testid="lead-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="voce@campanha.com"
                    className={inputCls}
                  />
                </Field>
                <Field label="Telefone / WhatsApp *">
                  <input
                    data-testid="lead-input-phone"
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="(61) 90000-0000"
                    className={inputCls}
                  />
                </Field>
                <Field label="Partido / organização">
                  <input
                    data-testid="lead-input-organization"
                    value={form.organization}
                    onChange={set("organization")}
                    placeholder="Sigla ou nome"
                    className={inputCls}
                  />
                </Field>
                <Field label="Candidato (se aplicável)">
                  <input
                    data-testid="lead-input-candidate"
                    value={form.candidate_name}
                    onChange={set("candidate_name")}
                    placeholder="Nome do candidato"
                    className={inputCls}
                  />
                </Field>
                <Field label="Estado">
                  <input
                    data-testid="lead-input-state"
                    value={form.state}
                    onChange={set("state")}
                    placeholder="UF · ex.: DF"
                    className={inputCls}
                  />
                </Field>
                <Field label="Tipo de disputa">
                  <select
                    data-testid="lead-input-scope"
                    value={form.scope}
                    onChange={set("scope")}
                    className={inputCls}
                  >
                    <option value="">Selecione…</option>
                    {SCOPES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Conte um pouco sobre a campanha" className="mt-4">
                <textarea
                  data-testid="lead-input-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={4}
                  placeholder="Tamanho da base, principais desafios, prazos…"
                  className={`${inputCls} resize-none`}
                />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="lead-form-submit"
                className="mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#208BFF] px-7 py-4 text-base font-semibold text-white shadow-sm transition-all duration-300 ease-out hover:bg-[#1B73D6] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 focus:outline-none focus:ring-2 focus:ring-[#208BFF] focus:ring-offset-2 focus:ring-offset-[#1B1D1D]"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Enviando…
                  </>
                ) : (
                  <>
                    Quero minha base na RSM
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              <p className="mt-4 text-xs text-[#F5FAFB]/55">
                Ao enviar, você concorda em receber retorno do time RSM Politics
                sobre a sua campanha. Seus dados não são compartilhados.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#F5FAFB]/55">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

const inputCls =
  "w-full rounded-xl border border-white/12 bg-[#0e0f0f] px-4 py-3 text-sm text-[#F5FAFB] placeholder:text-[#F5FAFB]/30 focus:border-[#208BFF] focus:outline-none focus:ring-2 focus:ring-[#208BFF]/30 transition-all";
